import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, QrCode, MessageSquareText, Award, ArrowRight, Ticket } from "lucide-react";
import SearchForm from "@/components/SearchForm";
import AppBanner from "@/components/AppBanner";
import { Crest, FlagStrip, OfficialBadge } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { money, todayStr } from "@/lib/format";
import Seo from "@/components/Seo";

const HERO = "/hero.jpg";

const FEATURES = [
  { icon: Smartphone, title: "Pay with MoMo", text: "MTN MoMo, Telecel Cash, AirtelTigo or card", tint: "bg-yellow-100 text-yellow-700" },
  { icon: QrCode, title: "QR e-tickets", text: "Every ticket carries a scannable QR code", tint: "bg-blue-100 text-[#0052FF]" },
  { icon: MessageSquareText, title: "Ticket by SMS", text: "Your ticket link arrives on your phone", tint: "bg-sky-100 text-sky-700" },
  { icon: Award, title: "Earn miles", text: "1 mile for every GH₵ 1 you spend", tint: "bg-emerald-100 text-emerald-700" },
];

function PartnerStrip() {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6">
      <div className="relative flex flex-col items-center gap-6 overflow-hidden rounded-3xl bg-[#182678] p-6 text-white sm:flex-row sm:p-8" data-testid="partner-strip">
        <Crest variant="white" className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 opacity-[0.07]" />
        <span className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-white"><Crest className="h-[72px] w-[72px]" /></span>
        <div className="flex-1 text-center sm:text-left">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FCD116]">Official union ticketing</div>
          <div className="mt-1 font-display text-2xl font-extrabold">One union. Every station. One ticket system.</div>
          <p className="mt-1 text-sm text-blue-100">Every ticket is issued by GPRTU stations and can be verified by scanning its QR code. The platform is developed and operated by Nacasky Co. Ltd.</p>
        </div>
        <a href="https://nacasky.com" target="_blank" rel="noreferrer" className="rounded-2xl bg-white px-5 py-3 text-center" data-testid="partner-nacasky-link">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Developed by</div>
          <img src="/brand/nacasky-logo.svg" alt="Nacasky Company Limited" className="mt-1 h-9 w-auto" />
        </a>
      </div>
    </section>
  );
}

export default function Home() {
  const nav = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [code, setCode] = useState("");
  useEffect(() => { api.get("/public/routes").then((r) => setRoutes(r.data)); }, []);
  const go = ({ from, to, date }) => nav(`/search?from=${from}&to=${to}&date=${date}`);

  return (
    <div>
      <Seo description="Book GPRTU intercity bus tickets online in Ghana with Quick Ride. Search routes like Accra–Kumasi and Accra–Cape Coast, choose your seat, pay by Mobile Money and get a QR e-ticket by SMS." path="/" />
      <section className="hero-bg relative overflow-hidden">
        <div className="dot-grid absolute inset-0 opacity-50" />
        <Crest variant="navy" className="pointer-events-none absolute -left-24 top-10 h-[520px] w-[520px] opacity-[0.03]" testid="hero-crest-watermark" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:pt-16">
          <div className="rise">
            <OfficialBadge />
            <h1 className="mt-4 font-display text-[1.75rem] font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl sm:leading-[1.05] lg:text-6xl">
              <span className="block">Your seat, your schedule,</span>
              <span className="block text-[#0052FF]">your Quick Ride.</span>
            </h1>
            <p className="mt-4 max-w-lg text-base text-slate-600 md:text-lg">Pick your route, choose your seat and pay with Mobile Money in under a minute.</p>
            <div className="mt-6 flex items-center gap-3" data-testid="hero-union-seal">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-white shadow-[0_10px_24px_-12px_rgba(24,38,120,0.6)] ring-1 ring-slate-200"><Crest className="h-12 w-12" /></span>
              <div className="leading-tight">
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#182678]">Ghana Private Road Transport Union</div>
                <div className="text-xs font-semibold text-slate-500">Union stations nationwide · United We Stand</div>
              </div>
            </div>
          </div>
          <div className="relative hidden lg:block rise" style={{ animationDelay: ".1s" }}>
            <img src={HERO} alt="GPRTU intercity coach on a Ghana highway" className="h-72 w-full rounded-[32px] object-cover shadow-[0_30px_60px_-30px_rgba(0,82,255,0.5)]" />
            <div className="absolute -bottom-5 left-6 rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
              <div className="label-xs">Today</div>
              <div className="font-display text-lg font-extrabold">{routes.length} routes · daily departures</div>
            </div>
          </div>
          <div className="lg:col-span-2 rise" style={{ animationDelay: ".15s" }}>
            <div className="overflow-hidden rounded-t-2xl"><FlagStrip /></div>
            <SearchForm onSearch={go} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="mb-5 flex items-end justify-between">
          <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Popular routes</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {routes.slice(0, 8).map((r, i) => (
            <button key={r.id} onClick={() => go({ from: r.origin_id, to: r.destination_id, date: todayStr() })}
              className="card-soft rise btn-press group flex items-center justify-between p-4 text-left hover:border-blue-300" style={{ animationDelay: `${i * 0.04}s` }} data-testid={`popular-route-${r.id}`}>
              <div>
                <div className="font-display text-base font-bold text-slate-900">{r.origin_city} → {r.destination_city}</div>
                <div className="text-xs text-slate-500">from <b className="text-[#0052FF]">{money(r.fare)}</b> · {r.distance_km} km</div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#0052FF]" />
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <div className="grid gap-3 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.title} className="card-soft flex items-start gap-3 p-5">
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${f.tint}`}><f.icon className="h-5 w-5" /></span>
                <div><div className="font-display font-bold">{f.title}</div><div className="text-sm text-slate-500">{f.text}</div></div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); code.trim() && nav(`/ticket/${code.trim().toUpperCase().split("-")[0]}`); }}
            className="rounded-2xl bg-[#0052FF] p-6 text-white shadow-[0_20px_40px_-20px_rgba(0,82,255,0.8)]">
            <Ticket className="h-7 w-7" />
            <div className="mt-3 font-display text-xl font-extrabold">Already booked?</div>
            <p className="text-sm text-blue-100">Enter your booking code to view, print or pay.</p>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. GPAB12CD" className="mt-4 h-11 border-0 bg-white font-mono font-bold uppercase text-slate-900" data-testid="home-booking-code-input" />
            <Button type="submit" className="mt-3 h-11 w-full rounded-xl bg-white font-bold text-[#0052FF] hover:bg-blue-50 btn-press" data-testid="home-find-booking-button">Find my ticket</Button>
          </form>
        </div>
      </section>
      <PartnerStrip />
      <AppBanner />
    </div>
  );
}
